// Ported from genieos/web/src/components/historian/Toast.tsx @ 84923d036
/**
 * Toast Notification Component for Historian Data Viewer
 *
 * Provides visual feedback for export operations and other actions.
 * Supports success, error, warning, and info message types.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertCircle, X, Info } from 'lucide-react';
import { Card } from '@/components/gamma';

export interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number; // ms, 0 for persistent
}

interface ToastProps {
  toast: ToastMessage;
  onDismiss: (id: string) => void;
}

const ICONS = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertCircle,
  info: Info,
};

const COLORS = {
  success: 'text-ems-green',
  error: 'text-ems-red',
  warning: 'text-ems-amber',
  info: 'text-ems-blue',
};

const BACKGROUNDS = {
  success: 'border-ems-green/30 bg-ems-green/10',
  error: 'border-ems-red/30 bg-ems-red/10',
  warning: 'border-ems-amber/30 bg-ems-amber/10',
  info: 'border-ems-blue/30 bg-ems-blue/10',
};

const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const Icon = ICONS[toast.type];

  useEffect(() => {
    if (toast.duration !== 0) {
      const timer = setTimeout(() => {
        onDismiss(toast.id);
      }, toast.duration || 4000);

      return () => clearTimeout(timer);
    }
  }, [toast.id, toast.duration, onDismiss]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.9 }}
      transition={{ duration: 0.2 }}
      role="alert"
      aria-live="polite"
    >
      <Card
        variant="elevated"
        className={`flex items-center gap-3 p-3 pr-4 shadow-lg min-w-[280px] border ${BACKGROUNDS[toast.type]}`}
      >
        <Icon
          className={`w-5 h-5 ${COLORS[toast.type]} flex-shrink-0`}
          aria-hidden="true"
        />
        <p className="text-ems-text-primary text-sm flex-1">{toast.message}</p>
        <button
          onClick={() => onDismiss(toast.id)}
          className="p-1 text-ems-text-secondary hover:text-ems-text-primary rounded-lg hover:bg-white/10 transition-colors"
          aria-label="Dismiss notification"
        >
          <X className="w-4 h-4" />
        </button>
      </Card>
    </motion.div>
  );
};

interface ToastContainerProps {
  toasts: ToastMessage[];
  onDismiss: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onDismiss,
}) => {
  return (
    <div
      className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2"
      role="region"
      aria-label="Notifications"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </AnimatePresence>
    </div>
  );
};

// Hook for managing toasts
export function useToasts() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback((toast: Omit<ToastMessage, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return { toasts, addToast, dismissToast, clearAllToasts };
}

export default ToastContainer;
